import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, TwIconButton, TwDropdown } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaSearch, FaArrowLeft, FaHome, FaTrashAlt, FaTrash, FaCheck,
  FaChevronLeft, FaChevronRight, FaUserCircle,
} from 'react-icons/fa';
import { MdRestore } from 'react-icons/md';
import {
  setCurrentPage, setPageSize, setCurrentParams, getTrashList,
  restoreFromTrash, deleteForever,
} from 'redux/drive/driveSlice';
import { debounce } from 'helpers/debounce';

import DriveDeleteConfirm from 'components/app/drive/DriveDeleteConfirm';
import { getFileMeta } from 'components/app/drive/fileTypes';

const PAGE_SIZE_OPTIONS = [
  { value: 50,  label: '50 / page'  },
  { value: 100, label: '100 / page' },
  { value: 200, label: '200 / page' },
  { value: 500, label: '500 / page' },
];

const TrashRow = ({ item, isSelected, onToggleSelect, onRestore, onDelete }) => {
  const { icon: Icon, color } = getFileMeta(item);
  const { allow_delete, allow_restore_from_trash } = item.permissions || {};

  return (
    <div
      className="tw-group"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px',
        borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
        borderBottom: '1px solid #f1f5f9',
        backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
        cursor: 'default',
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
    >
      <button
        type="button"
        onClick={() => onToggleSelect(item)}
        aria-label={isSelected ? 'Deselect' : 'Select'}
        style={{
          appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
          width: 16, height: 16, padding: 0, margin: 0, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: isSelected ? '#2563eb' : '#ffffff',
          color: isSelected ? '#ffffff' : 'transparent',
          border: `1.5px solid ${isSelected ? '#2563eb' : '#94a3b8'}`,
          borderRadius: 4, cursor: 'pointer',
        }}
      >
        {isSelected && <FaCheck size={8} />}
      </button>

      <div style={{
        width: 36, height: 36, flexShrink: 0,
        backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
        borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} style={{ color }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 13, fontWeight: 700, color: '#0f172a',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4,
        }} title={item.name}>
          {item.name}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
          {item.asset_type === 'folder' ? 'Folder' : (item.content_type || 'File')}
        </p>
      </div>

      <div className="tw-hidden md:tw-flex" style={{ width: 180, alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <FaUserCircle size={12} style={{ color: '#94a3b8' }} />
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.trash_by || '—'}
        </span>
      </div>

      <div className="tw-hidden lg:tw-block" style={{
        width: 140, fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        flexShrink: 0, fontWeight: 500,
      }}>
        {item.trash_date || '—'}
      </div>

      <div
        className="tw-opacity-0 group-hover:tw-opacity-100"
        style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, transition: 'opacity 150ms' }}
      >
        {allow_restore_from_trash && (
          <TwIconButton icon={MdRestore} size="sm" variant="secondary" onClick={() => onRestore(item)} title="Restore" />
        )}
        {allow_delete && (
          <TwIconButton icon={FaTrash} size="sm" variant="danger" onClick={() => onDelete(item)} title="Delete forever" />
        )}
      </div>
    </div>
  );
};

TrashRow.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    asset_type: PropTypes.string,
    content_type: PropTypes.string,
    trash_by: PropTypes.string,
    trash_date: PropTypes.string,
    permissions: PropTypes.shape({
      allow_delete: PropTypes.bool,
      allow_restore_from_trash: PropTypes.bool
    })
  }).isRequired,
  isSelected: PropTypes.bool,
  onToggleSelect: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

const DriveTrashPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, totalRows, paginate, currentParams } = useSelector((s) => s.drive);
  const { totalPagesCount, currentPage, pageSize, fromRecord, toRecord } = paginate;

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);   // single asset
  const [deleteTarget, setDeleteTarget]   = useState(null);   // single asset | 'bulk'

  const selectedAssets = useMemo(
    () => data.filter((d) => selectedIds.includes(d.id)),
    [data, selectedIds],
  );

  /* ─── viewport offsets ─── */
  const [pageOffsets, setPageOffsets] = useState({ top: 60, left: 0, right: 0, bottom: 0 });
  useEffect(() => {
    const calc = () => {
      const navbarTop      = document.querySelector('.ptl-navbar-top');
      const navbarVertical = document.querySelector('.ptl-navbar-vertical');
      const banner         = document.querySelector('.release-notification-banner');
      const topNavBottom = navbarTop ? navbarTop.getBoundingClientRect().bottom : 0;
      const bannerBottom = banner    ? banner.getBoundingClientRect().bottom : 0;
      const top = Math.max(topNavBottom, bannerBottom, 60);
      const sidebar = navbarVertical?.getBoundingClientRect();
      const left = sidebar && sidebar.right > 0 && sidebar.width < window.innerWidth ? sidebar.right : 0;
      setPageOffsets({ top, left, right: 0, bottom: 0 });
    };
    calc();
    window.addEventListener('resize', calc);
    const sb = document.querySelector('.ptl-navbar-vertical');
    let mo;
    if (sb && window.MutationObserver) {
      mo = new window.MutationObserver(calc);
      mo.observe(sb, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    return () => {
      window.removeEventListener('resize', calc);
      mo?.disconnect();
    };
  }, []);

  /* ─── data ─── */
  useEffect(() => {
    dispatch(setCurrentParams({ page: 1, per_page: 100 }));
    return () => { setSelectedIds([]); };
  }, [dispatch]);

  useEffect(() => {
    if (currentParams) dispatch(getTrashList(currentParams));
  }, [currentParams, dispatch]);

  /* ─── handlers ─── */
  const debouncedSearch = useMemo(
    () => debounce((value) => {
      dispatch(setCurrentParams({ ...currentParams, name_cont: value, page: 1 }));
    }, 400),
    [currentParams, dispatch],
  );

  const handleSearch = (e) => { setSearch(e.target.value); debouncedSearch(e.target.value); };
  const clearSearch  = () => { setSearch(''); debouncedSearch(''); };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
    dispatch(setCurrentParams({ ...currentParams, page, per_page: pageSize }));
  };

  const handlePageSizeChange = (newSize) => {
    dispatch(setPageSize(newSize));
    dispatch(setCurrentParams({ ...currentParams, page: 1, per_page: newSize }));
  };

  const toggleSelect = (item) => {
    setSelectedIds((prev) =>
      prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id],
    );
  };

  const isAllSelected = data.length > 0 && data.every((d) => selectedIds.includes(d.id));
  const toggleSelectAll = () => setSelectedIds(isAllSelected ? [] : data.map((d) => d.id));

  const performBulkDelete = useCallback(async () => {
    for (const item of selectedAssets) {
      if (item.permissions?.allow_delete) {
        await dispatch(deleteForever(item.id)).unwrap().catch(() => {});
      }
    }
    setSelectedIds([]);
    dispatch(getTrashList(currentParams));
  }, [selectedAssets, dispatch, currentParams]);

  const performRestore = useCallback(async () => {
    if (!restoreTarget) return;
    await dispatch(restoreFromTrash(restoreTarget.id)).unwrap().catch(() => {});
    setRestoreTarget(null);
    dispatch(getTrashList(currentParams));
  }, [restoreTarget, dispatch, currentParams]);

  const performDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget === 'bulk') return;
    await dispatch(deleteForever(deleteTarget.id)).unwrap().catch(() => {});
    setDeleteTarget(null);
    dispatch(getTrashList(currentParams));
  }, [deleteTarget, dispatch, currentParams]);

  const totalPages = totalPagesCount || 0;

  return (
    <div
      className="tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden tw-bg-slate-100"
      style={{
        position: 'fixed',
        top: pageOffsets.top, left: pageOffsets.left, right: pageOffsets.right, bottom: pageOffsets.bottom,
        zIndex: 1,
      }}
    >
      {/* ── HEADER ─────────────────────────────────────── */}
      <div className="tw-flex-shrink-0 tw-bg-white tw-z-30">
        <div className="tw-px-4 sm:tw-px-6 tw-pt-4 tw-pb-3">
          <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3">
            <div className="tw-flex tw-items-baseline tw-gap-3">
              <h1
                className="tw-text-2xl tw-font-black tw-tracking-tighter tw-m-0"
                style={{
                  backgroundImage: 'linear-gradient(to right, #0f172a, #dc2626)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Trash
              </h1>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  paddingLeft: 12, paddingRight: 8,
                  height: 36, width: 280,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                }}
              >
                <FaSearch style={{ color: '#94a3b8', flexShrink: 0 }} size={11} />
                <input
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder="Search trash..."
                  style={{
                    appearance: 'none', WebkitAppearance: 'none',
                    boxShadow: 'none', margin: 0, padding: 0,
                    border: 'none', outline: 'none',
                    backgroundColor: 'transparent',
                    flex: 1, minWidth: 0,
                    fontSize: 12, fontWeight: 500,
                    color: '#0f172a', fontFamily: 'inherit', lineHeight: 1.4,
                  }}
                />
                {search && (
                  <button type="button" onClick={clearSearch} style={{
                    appearance: 'none', WebkitAppearance: 'none',
                    width: 18, height: 18, padding: 0, margin: 0,
                    backgroundColor: 'transparent', border: 'none',
                    color: '#94a3b8', cursor: 'pointer', fontSize: 12,
                  }} aria-label="Clear">×</button>
                )}
              </div>

              <Button variant="secondary" size="sm" leftIcon={FaArrowLeft} onClick={() => navigate('/drives/list')}>
                <span className="tw-hidden md:tw-inline">Back to Drive</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Sub-toolbar */}
        <div className="tw-px-4 sm:tw-px-6 tw-pb-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <nav className="tw-flex tw-items-center tw-gap-1 tw-min-w-0 tw-flex-1" aria-label="Breadcrumb">
            <Link
              to="/drives/list"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '0 8px', height: 24, borderRadius: 6,
                fontSize: 12, fontWeight: 700, color: '#64748b', textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
            >
              <FaHome size={16} /> <span></span>
            </Link>
            <span style={{ color: '#222222', fontSize: 20 }}>›</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '0 8px', height: 24,
              fontSize: 12, fontWeight: 700, color: '#0f172a',
            }}>
              <FaTrashAlt size={10} /> Trash
            </span>
          </nav>

          <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
            <button
              type="button"
              onClick={toggleSelectAll}
              disabled={data.length === 0}
              style={{
                appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                height: 32, padding: '0 12px 0 10px',
                backgroundColor: isAllSelected ? '#2563eb' : '#ffffff',
                color: isAllSelected ? '#ffffff' : '#475569',
                border: `1px solid ${isAllSelected ? '#2563eb' : '#e2e8f0'}`,
                borderRadius: 8, cursor: data.length === 0 ? 'not-allowed' : 'pointer',
                opacity: data.length === 0 ? 0.5 : 1,
                fontSize: 12, fontWeight: 700, fontFamily: 'inherit', lineHeight: 1.4,
              }}
            >
              <span style={{
                width: 16, height: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: isAllSelected ? '#ffffff' : 'transparent',
                color: isAllSelected ? '#2563eb' : 'transparent',
                border: `1.5px solid ${isAllSelected ? '#ffffff' : '#94a3b8'}`,
                borderRadius: 4,
              }}>
                {isAllSelected && <FaCheck size={8} />}
              </span>
              Select all
            </button>

            <TwDropdown value={pageSize} onChange={handlePageSizeChange} options={PAGE_SIZE_OPTIONS} size="sm" minWidth="120px" />
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div className="tw-px-4 sm:tw-px-6 tw-py-2 tw-bg-slate-900 tw-text-white tw-flex tw-items-center tw-gap-3">
            <span className="tw-text-xs tw-font-bold">{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
            <button type="button" onClick={() => setSelectedIds([])} className="tw-text-xs tw-font-bold tw-text-slate-400 hover:tw-text-white tw-bg-transparent tw-border-0 tw-cursor-pointer">
              Clear
            </button>
            <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
              <Button variant="danger" size="sm" leftIcon={FaTrash} onClick={() => setDeleteTarget('bulk')}>
                Delete forever
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── BODY (fills available height, table fills the body) ─── */}
      <div
        style={{
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          borderTop: '1px solid #e2e8f0',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          padding: '20px 24px',
        }}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            flex: 1, minHeight: 0,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* table header (sticky inside scroll area) */}
          <div className="tw-hidden sm:tw-flex" style={{
            flexShrink: 0,
            alignItems: 'center', gap: 12,
            padding: '10px 14px',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontSize: 10, fontWeight: 700, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            <div style={{ width: 18, flexShrink: 0 }} />
            <div style={{ width: 36, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>Name</div>
            <div className="tw-hidden md:tw-block" style={{ width: 180, flexShrink: 0 }}>Deleted by</div>
            <div className="tw-hidden lg:tw-block" style={{ width: 140, flexShrink: 0 }}>Deleted on</div>
            <div style={{ width: 76, flexShrink: 0, textAlign: 'right' }}>Actions</div>
          </div>

          {/* rows scroll area (fills card height) */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {data && data.length > 0 ? (
              data.map((item) => (
                <TrashRow
                  key={item.id}
                  item={item}
                  isSelected={selectedIds.includes(item.id)}
                  onToggleSelect={toggleSelect}
                  onRestore={setRestoreTarget}
                  onDelete={setDeleteTarget}
                />
              ))
            ) : (
              <div style={{
                height: '100%', minHeight: 280,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', padding: '32px',
              }}>
                <div style={{
                  width: 72, height: 72, marginBottom: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: '#f1f5f9', borderRadius: 16,
                }}>
                  <FaTrashAlt size={28} style={{ color: '#cbd5e1' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Trash is empty</h3>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b', fontWeight: 500, maxWidth: 320 }}>
                  Items moved to trash will appear here. They can be restored or deleted permanently.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── FOOTER ─────────────────────────────── */}
      {totalRows > 0 && (
        <div className="tw-flex-shrink-0 tw-bg-white tw-px-4 sm:tw-px-6 tw-py-2 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#64748b', fontWeight: 500 }}>
            <span className="tw-hidden sm:tw-inline">
              Showing&nbsp;
              <strong style={{ color: '#0f172a', fontWeight: 700, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{fromRecord}–{toRecord}</strong>
              &nbsp;of&nbsp;
              <strong style={{ color: '#0f172a', fontWeight: 700, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{totalRows}</strong>
            </span>
            <span className="sm:tw-hidden" style={{ fontWeight: 600 }}>
              <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{totalRows}</strong> items
            </span>
            <span aria-hidden="true" className="tw-hidden md:tw-block" style={{ width: 1, height: 16, backgroundColor: '#cbd5e1' }} />
            <span className="tw-hidden md:tw-inline-flex" style={{
              alignItems: 'center', gap: 4, padding: '2px 8px',
              backgroundColor: '#fef2f2', color: '#dc2626',
              border: '1px solid #fecaca', borderRadius: 999,
              fontWeight: 700, fontSize: 11,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            }}>
              {totalRows}
              <span style={{ fontFamily: 'inherit', fontWeight: 600, opacity: 0.8 }}>deleted</span>
            </span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <TwIconButton icon={FaChevronLeft}  size="sm" variant="secondary" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1} title="Previous page" />
            <span className="tw-text-xs tw-font-mono tw-font-bold tw-text-slate-700 tw-px-2 tw-tabular-nums">{currentPage} / {totalPages || 1}</span>
            <TwIconButton icon={FaChevronRight} size="sm" variant="secondary" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} title="Next page" />
          </div>
        </div>
      )}

      {/* ── MODALS ─────────────────────────────────── */}
      <DriveDeleteConfirm
        show={Boolean(restoreTarget)}
        onClose={() => setRestoreTarget(null)}
        onConfirm={performRestore}
        title="Restore from trash"
        message="Restore this item back to its original location?"
        itemName={restoreTarget?.name}
        confirmLabel="Restore"
      />

      <DriveDeleteConfirm
        show={Boolean(deleteTarget) && deleteTarget !== 'bulk'}
        onClose={() => setDeleteTarget(null)}
        onConfirm={performDelete}
        title="Delete forever"
        message="This item will be permanently deleted. This action cannot be undone."
        itemName={deleteTarget?.name}
        confirmLabel="Delete forever"
      />

      <DriveDeleteConfirm
        show={deleteTarget === 'bulk'}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await performBulkDelete(); setDeleteTarget(null); }}
        title="Delete forever"
        message={`Permanently delete ${selectedAssets.length} item(s)? This cannot be undone.`}
        itemNames={selectedAssets.map((a) => a.name)}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

export default DriveTrashPage;
